import { useCallback, useMemo } from 'react';
import { V1 as AiSettingsV1 } from '@frontend/api-ai-settings';
import { formatDate } from '@frontend/date';
import { useAppScopeStore } from '@frontend/scope';
import { useFormField } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../constants';
import { getMaxCalendarDate } from '../../../utils';
import { getTodaysDate, getWeekRange, startOfWeek } from '../../../utils/date-helpers';
import {
  AvailabilityFilters,
  AvailabilityHeaderFieldProps,
  TIME_RANGES,
  UseAvailabilityFiltersArgs,
} from './availability-header.types';

export const useAvailabilityFilters = ({ locationId, orgId, initialServiceId }: UseAvailabilityFiltersArgs) => {
  const { selectedLocationIds, getScopeName, isSingleTypeScope } = useAppScopeStore();
  const isMultiLocation = !isSingleTypeScope;

  const { data: appointmentTypesData } = AiSettingsV1.Queries.useGetSchedulingAppointmentTypesQuery({
    request: { locationId, orgId },
    options: { enabled: !!locationId && !!orgId },
  });

  const appointmentTypes = appointmentTypesData?.appointmentTypes ?? [];
  const serviceSelectorField = useFormField({ type: 'text', value: initialServiceId ?? '' });
  const selectedServiceId = serviceSelectorField.value || appointmentTypes[0]?.id || '';

  const onServiceSelect = useCallback(
    (id: string) => serviceSelectorField.onChange({ value: id, name: 'serviceId' }),
    [serviceSelectorField]
  );

  const { todayDate, currentWeekStartDate }: { todayDate: string; currentWeekStartDate: string } = useMemo(
    () => ({
      todayDate: getTodaysDate(),
      currentWeekStartDate: startOfWeek(getTodaysDate()).format(DATETIME_FORMATS.US_DATE),
    }),
    []
  );

  const weekDatePicker = useFormField(
    { type: 'datePicker', value: todayDate, required: true, maxDate: getMaxCalendarDate() },
    []
  );

  const { weekStartDate, weekEndDate, isCurrentWeek, goToNextWeek, goToThisWeek, goToPreviousWeek } = useMemo(() => {
    const weekDateRange = getWeekRange(weekDatePicker.value);
    const start = weekDateRange.startOfWeek.format(DATETIME_FORMATS.US_DATE);
    const end = weekDateRange.endOfWeek.format(DATETIME_FORMATS.US_DATE);

    return {
      weekStartDate: start,
      weekEndDate: end,
      isCurrentWeek: start === currentWeekStartDate,
      goToThisWeek: () => weekDatePicker.onChange({ name: 'weekDatePicker', value: todayDate }),
      goToNextWeek: () =>
        weekDatePicker.onChange({
          name: 'weekDatePicker',
          value: startOfWeek(weekDatePicker.value).add(7, 'day').format(DATETIME_FORMATS.US_DATE),
        }),
      goToPreviousWeek: () =>
        weekDatePicker.onChange({
          name: 'weekDatePicker',
          value: startOfWeek(weekDatePicker.value).subtract(7, 'day').format(DATETIME_FORMATS.US_DATE),
        }),
    };
  }, [weekDatePicker, currentWeekStartDate, todayDate]);

  const { data: resourcesData } = AiSettingsV1.Queries.useGetSchedulingResourcesQuery({
    request: { locationId, orgId },
    options: { enabled: !!locationId && !!orgId },
  });

  const operatories = useMemo(
    () =>
      (resourcesData?.resources ?? []).map((operatory) => ({
        value: operatory.id ?? '',
        label: operatory.name ?? operatory.id ?? '',
      })),
    [resourcesData?.resources]
  );
  const operatoryChecklistField = useFormField({ type: 'checklist', value: [] });

  const { data: staffsData } = AiSettingsV1.Queries.useGetSchedulingStaffsQuery({
    request: { locationId, orgId },
    options: { enabled: !!locationId && !!orgId },
  });
  const providers = useMemo(
    () =>
      (staffsData?.staffs ?? []).map((provider) => ({
        value: provider.id ?? '',
        label: provider.name ?? provider.id ?? '',
      })),
    [staffsData?.staffs]
  );
  const providerChecklistField = useFormField({ type: 'checklist', value: [] });

  const locationOptions = useMemo(
    () => selectedLocationIds.map((id) => ({ value: id, label: getScopeName(id) })),
    [selectedLocationIds, getScopeName]
  );
  const locationChecklistField = useFormField({ type: 'checklist', value: [] });

  const timeRangeOptions = useMemo(
    () =>
      TIME_RANGES.map((timeRange) => ({
        value: timeRange.id,
        label: `${timeRange.label} (${timeRange.extendedLabel})`,
      })),
    []
  );

  const timeRangeChecklistField = useFormField({ type: 'checklist', value: [] });

  const resolvedFilters: AvailabilityFilters = useMemo(() => {
    const resolvedOperatoryIds =
      operatoryChecklistField.value.length > 0 ? operatoryChecklistField.value : operatories.map((o) => o.value);
    const resolvedProviderIds =
      providerChecklistField.value.length > 0 ? providerChecklistField.value : providers.map((p) => p.value);
    const resolvedLocationIds = locationChecklistField.value.length > 0 ? locationChecklistField.value : [locationId];
    const resolvedTimeRanges =
      timeRangeChecklistField.value.length > 0
        ? TIME_RANGES.filter((timeRange) => timeRangeChecklistField.value.includes(timeRange.id))
        : TIME_RANGES;
    return {
      serviceId: selectedServiceId,
      locationIds: resolvedLocationIds,
      timeRanges: resolvedTimeRanges,
      operatoryIds: resolvedOperatoryIds,
      providerIds: resolvedProviderIds,
      weekStartDate: formatDate(weekStartDate, DATETIME_FORMATS.ISO_DATE),
      weekEndDate: formatDate(weekEndDate, DATETIME_FORMATS.ISO_DATE),
    };
  }, [
    selectedServiceId,
    operatoryChecklistField.value,
    providerChecklistField.value,
    locationChecklistField.value,
    timeRangeChecklistField.value,
    weekStartDate,
    weekEndDate,
    locationId,
    operatories,
    providers,
  ]);

  const headerFieldProps: AvailabilityHeaderFieldProps = {
    weekInView: weekDatePicker.value,
    weekDatePicker,
    handleDateChange: weekDatePicker.onChange,
    goToNextWeek,
    goToPreviousWeek,
    goToThisWeek,
    isCurrentWeek,
    selectedServiceId: selectedServiceId || undefined,
    onServiceSelect,
    isMultiLocation,
    locationOptions,
    locationChecklistField,
    timeRangeOptions,
    timeRangeChecklistField,
    operatories,
    operatoryChecklistField,
    providers,
    providerChecklistField,
  };

  return { resolvedFilters, headerFieldProps };
};
